/**
 * PixiJS renderer. Subscribes to sim state, interpolates entity visuals
 * between the previous and current logic tick using `alpha ∈ [0, 1)`.
 *
 * The sim never imports this file. See CLAUDE.md architecture rule 4.
 */

import { Application, BlurFilter, Container, Sprite, Texture } from 'pixi.js';
import { CONFIG } from '../config';
import type { FovMemory } from '../sim/fovMemory';
import type { Guard } from '../sim/guard';
import type { MapData } from '../sim/map';
import { blindIntensity } from '../sim/blind';
import { isUnlit, lightLevelAt } from '../sim/light';
import type { Player } from '../sim/player';
import type { Entity, EntityId, World } from '../sim/world';
import type { Bullet } from '../sim/bullet';
import { AmbientNoiseLayer } from './ambientNoiseLayer';
import { buildAtlas, type GlyphAtlas } from './atlas';
import type { Camera } from './camera';
import { BloodLayer } from './bloodLayer';
import { BulletsLayer } from './bulletsLayer';
import { CrtOverlay } from './crtOverlay';
import { FogLayer } from './fogLayer';
import { buildMapLayer, type MapSprites } from './mapRenderer';
import { LAYER_Z_STEP_PX } from './style';
import { loadSettings } from '../settings';
import { PerceptionOverlay } from './perceptionOverlay';
import type { SoundFieldCell } from './soundField';
import type { VisionFieldCell } from './visionField';
import { BLOOM_ALPHA, BLOOM_BLUR_QUALITY, BLOOM_BLUR_STRENGTH, BLOOM_HERO_BOOST, BLOOM_SCALE, BOB_AMPLITUDE_PX, BOB_FREQ_BASE_HZ, BOB_FREQ_SPEED_MULT, GHOST_ALPHA, GHOST_FLOAT_HZ, GHOST_FLOAT_PX, GHOST_TINT, LEG_ANIM_BASE_HZ, LEG_ANIM_FRAMES, LEG_ANIM_SPEED_MULT, STACK_ALPHA_RAISED, STACK_DARKEN_PER_LAYER, STACK_HALO_BOOST, WEAPON_SUIT_GLYPHS, WEAPON_SUIT_REACH, WEAPON_SUIT_TINTS } from './style';

interface SpriteEntry {
  glyph: Sprite;
  halo: Sprite;
  bg: Sprite | null;
  currentChar: string;
  /** Horizontal offset within the tile to center a non-square glyph. */
  glyphOffsetX: number;
  /** Accumulated walk-bob phase in radians. Advances only while the entity
   *  is visibly moving; frozen when idle so the bob doesn't snap on stop. */
  bobPhase: number;
  /** Last-frame visual position — used to detect motion for the bob. */
  lastVisualX: number;
  lastVisualY: number;
  /** The LEGS sprite (glyph `ハ`/`メ`) rendered at the tile, with the torso
   *  (`glyph`) stacked one tile screen-up above it. Created for every entity
   *  (player + guards); nullable only defensively. */
  legsGlyph: Sprite | null;
  legsHalo: Sprite | null;
  /** Current legs walk-cycle char, so we only swap the texture on change. */
  legsChar: string;
  /** Accumulated legs walk-cycle phase (radians); frozen when idle. */
  legsPhase: number;
  /** Held-weapon suit glyph (player only) shown in focus mode — the card suit
   *  of the active weapon (♣ ♦ ♠ ♥), aimed at the locked target (heal stays
   *  upright). Hidden otherwise. */
  weaponGlyph: Sprite | null;
  weaponChar: string;
}

/** Snapshot of what the renderer needs beyond raw entities to draw a frame. */
export interface RenderView {
  world: World;
  camera: Camera;
  alpha: number;
  fov: FovMemory;
  player: Player;
  guards: readonly Guard[];
  map: MapData;
  /** Per-tile sound-field overlay cells (instant, computed from the same
   *  flood guards hear through). Drawn as an additive rainbow highlight. */
  soundField: Map<number, SoundFieldCell>;
  /** Per-tile guard-vision-cone cells (union of visible guards' FOV). Each tile
   *  glyph is multiplied toward blue by the cell intensity so watched tiles read
   *  as danger. Same tile-colouring technique as the sound field. */
  visionField: Map<number, VisionFieldCell>;
  /** Frame delta seconds — used by the walk-bob to advance per-entity
   *  bob phase in real time (independent of the fixed sim tick rate). */
  dt: number;
  /** Active projectile bullets — drawn as ',' glyphs with a bloom trail. */
  bullets: readonly Bullet[];
  /** Player night vision active — brightens tiles by `nvBoost`, blows out bright
   *  tiles toward white, and tints the screen green + monochrome. */
  nightVision: boolean;
}

export class Renderer {
  readonly app: Application;
  readonly world: Container;
  private readonly mapBgLayer: Container;
  private readonly mapBloomLayer: Container;
  private readonly mapFgLayer: Container;
  private readonly entityBgLayer: Container;
  private readonly entityBloomLayer: Container;
  private readonly entityFgLayer: Container;
  private readonly ambientNoise: AmbientNoiseLayer;
  private crt!: CrtOverlay;
  /** Sprites for guard "ghosts" — desaturated marks at last-seen positions. */
  private readonly ghostSprites = new Map<EntityId, Sprite>();
  /** Small '!' exponent marker over alert guards (0.6× tile scale). */
  private readonly alertMarks = new Map<EntityId, Sprite>();
  /** '#' marks shown over a guard while it's alerting other guards. */
  private readonly alertingMarks = new Map<EntityId, Sprite>();
  private atlas!: GlyphAtlas;
  perception!: PerceptionOverlay;
  fog!: FogLayer;
  bloodLayer!: BloodLayer;
  bullets!: BulletsLayer;
  private mapSprites: MapSprites | null = null;
  /** Tile indices whose glyph got a sound-hue multiply on the previous frame
   *  — reset them to their base tint before applying this frame's tint. */
  private prevSoundTiles: number[] = [];
  /** Tile indices whose glyph got a vision-cone blue multiply last frame — reset
   *  to base before re-applying this frame (same pattern as the sound field). */
  private prevVisionTiles: number[] = [];
  readonly tileSize: number;
  private readonly sprites = new Map<EntityId, SpriteEntry>();
  /** Per-tile lit base tint (ground glyph) = defaultFg × light brightness.
   *  Recomputed when the light field changes; the sound-hue pass resets tiles
   *  to this (so sound tint layers on top of lighting). */
  private litFgByIdx: (number | null)[] = [];
  private lastLightVersion = -1;
  /** Container of light-fixture marker glyphs (inside `world`, under fog). */
  private lightLayer!: Container;
  private lightFixtures: { sprite: Sprite; roomId: number }[] = [];
  /** Additive muzzle-flash glow sprites (gunfire light), pooled. */
  private muzzleLayer!: Container;
  private muzzleSprites: Sprite[] = [];
  /** Tile indices brightened by a guard flashlight last frame — reset to their
   *  lit base before re-applying, like the sound-field pass. */
  private prevFlashTiles: number[] = [];
  /** Player night-vision state this frame + last-applied, to force a full tile
   *  re-tint only when it toggles (the light→colour curve changes). */
  private nightVision = false;
  private lastNvApplied = false;
  /** Screen-shake setting, read once at init (settings only change from the menu,
   *  before the game boots). Gates the damage jitter on glyphs. */
  private screenShake = true;
  /** White glare veil (night vision): pooled per-visible-tile quads whose alpha
   *  is the tile's overexposure, occluding what's rendered under bright light. */
  private glareLayer!: Container;
  private readonly glarePool: Sprite[] = [];
  /** Player-visible Sniper-Elite "seen" ghosts — a pale '@' at each guard's
   *  last-seen-player tile while its sighting ghost is alive. */
  private sightingGhostLayer!: Container;
  private readonly sightingGhostPool: Sprite[] = [];
  /** The player's corpse: a downed '@' pinned to the death tile while the player
   *  roams as a ghost. Null until first needed. */
  private playerCorpseSprite: Sprite | null = null;
  private playerCorpseLegsSprite: Sprite | null = null;

  constructor(app: Application) {
    this.app = app;
    this.tileSize = CONFIG.render.tileSize;
    this.world = new Container();
    this.mapBgLayer = new Container();
    this.mapBloomLayer = new Container();
    this.mapFgLayer = new Container();
    this.entityBgLayer = new Container();
    this.entityBloomLayer = new Container();
    this.entityFgLayer = new Container();
    this.mapFgLayer.sortableChildren = true;
    this.mapBloomLayer.sortableChildren = true;
    this.entityFgLayer.sortableChildren = true;
    this.entityBloomLayer.sortableChildren = true;
    // Cyberpunk glow — Pixi BlurFilter on both bloom layers smears the
    // halo sprites into a soft neon aura around each glyph, additively
    // blended into the frame.
    const mapBlur = new BlurFilter({ strength: BLOOM_BLUR_STRENGTH, quality: BLOOM_BLUR_QUALITY });
    const entityBlur = new BlurFilter({ strength: BLOOM_BLUR_STRENGTH, quality: BLOOM_BLUR_QUALITY });
    this.mapBloomLayer.filters = [mapBlur];
    this.entityBloomLayer.filters = [entityBlur];
    this.ambientNoise = new AmbientNoiseLayer();
    // Ambient noise sits under everything, viewport-anchored (not
    // camera-transformed) so it stays visible even when you look at map
    // edges / void.
    this.app.stage.addChild(this.ambientNoise.container);
    this.lightLayer = new Container();
    this.muzzleLayer = new Container();
    this.glareLayer = new Container();
    this.sightingGhostLayer = new Container();
    this.world.addChild(this.mapBgLayer);
    this.world.addChild(this.mapBloomLayer);
    this.world.addChild(this.mapFgLayer);
    this.world.addChild(this.lightLayer);
    this.world.addChild(this.muzzleLayer);
    this.world.addChild(this.entityBgLayer);
    this.world.addChild(this.entityBloomLayer);
    this.world.addChild(this.entityFgLayer);
    this.app.stage.addChild(this.world);
  }

  init(): void {
    this.atlas = buildAtlas({
      fontPx: CONFIG.render.atlasFontPx,
      fontFamily: CONFIG.render.atlasFontFamily,
    });
    this.fog = new FogLayer();
    this.bloodLayer = new BloodLayer();
    this.bullets = new BulletsLayer();
    this.bullets.init(this.atlas, this.tileSize);
    this.perception = new PerceptionOverlay(this.atlas, this.tileSize);
    this.crt = new CrtOverlay(this.app);

    // Layer order (bottom → top): map bg/bloom/fg, blood pools, perception
    // debug, entity bg/bloom/fg, fog mask, perception hud, focus. CRT scanline
    // overlay sits on top of the stage, outside `world`, so it doesn't move with
    // the camera. Neither sound NOR guard vision cones are layers — each
    // multiplies the map glyph tints directly (see draw()), and the fog reveals
    // their tiles so they're bright regardless of FOV.
    this.world.addChild(this.bloodLayer.container);
    this.world.addChild(this.perception.tileLayer);
    this.world.setChildIndex(this.entityBgLayer, this.world.children.length - 1);
    this.world.setChildIndex(this.entityBloomLayer, this.world.children.length - 1);
    this.world.setChildIndex(this.entityFgLayer, this.world.children.length - 1);
    this.world.addChild(this.bullets.container);
    this.world.addChild(this.fog.container);
    // Glare veil sits above map + entities + fog so bright light occludes them,
    // but under the perception HUD so debug markers stay legible.
    this.world.addChild(this.glareLayer);
    // Sniper-Elite "seen" ghosts above the fog so the player sees them even
    // outside their own FOV (it's the player's own "you were seen here" cue).
    this.world.addChild(this.sightingGhostLayer);
    this.world.addChild(this.perception.hudLayer);
    this.world.addChild(this.perception.focusLayer);
    this.app.stage.addChild(this.crt.sprite);
    // Player settings (from the title screen): CRT overlay + screen shake.
    const settings = loadSettings();
    this.crt.sprite.visible = settings.crt;
    this.screenShake = settings.screenShake;
    this.ambientNoise.setCellSize(this.tileSize);
    this.ambientNoise.build(this.atlas, this.app.renderer.width, this.app.renderer.height);
  }

  /** Recompute viewport-anchored layers when the window resizes. Call from
   *  the app ticker if the browser has been resized. */
  handleResize(): void {
    const w = this.app.renderer.width;
    const h = this.app.renderer.height;
    this.ambientNoise.resize(w, h);
    this.crt.resize(w, h);
  }

  /** Populate the static map layer + fog sprites. Call once after map creation. */
  loadMap(map: MapData): void {
    this.rebuildMapLayer(map);
    this.fog.build(map, this.tileSize);
    this.bloodLayer.build(map.width, map.height, this.tileSize);
    this.buildLightFixtures(map);
    this.lastLightVersion = -1; // force a light re-tint on the next draw
  }

  /** One marker glyph per fixture, positioned at its tile. Appearance (on/off)
   *  is set in `applyLighting`. */
  private buildLightFixtures(map: MapData): void {
    this.lightLayer.removeChildren();
    this.lightFixtures = [];
    const scale = this.tileSize / this.atlas.cellH;
    for (const src of map.lights) {
      const sprite = new Sprite(this.atlas.get('☼') ?? Texture.EMPTY);
      sprite.anchor.set(0.5, 0.5);
      sprite.scale.set(scale * 0.8);
      sprite.x = (src.x + 0.5) * this.tileSize;
      sprite.y = (src.y + 0.5) * this.tileSize;
      this.lightLayer.addChild(sprite);
      this.lightFixtures.push({ sprite, roomId: src.roomId });
    }
  }

  /** Rebuild only the static tile sprites (not fog/blood). Used on load and
   *  whenever tiles change at runtime (e.g. a destructible prop shot to
   *  rubble). Cheap enough to run whole-map since destruction is infrequent. */
  private rebuildMapLayer(map: MapData): void {
    this.mapSprites = buildMapLayer(
      map,
      this.atlas,
      this.tileSize,
      this.mapFgLayer,
      this.mapBgLayer,
      this.mapBloomLayer,
    );
  }

  draw(view: RenderView): void {
    const { world, camera, alpha, fov, player, guards, map, soundField, visionField, dt, bullets } = view;
    // A destructible prop turned to rubble (or any runtime tile change) marks
    // tiles dirty; rebuild the static map layer once, then clear.
    if (map.dirtyTiles.size > 0) {
      this.rebuildMapLayer(map);
      map.dirtyTiles.clear();
    }
    this.bullets.update(bullets);
    // Apply pivot + rotation. Pivot is in the container's LOCAL (pre-scale)
    // coord space — glyphs are drawn at (visualX + 0.5) * tileSize, so
    // the pivot must use raw tileSize (no zoom factor) for the pivot tile
    // to end up exactly at world.position. Rotation and scale happen around
    // that pivot, so the world spins around whatever we're pivoting on
    // (player or focus target).
    this.world.pivot.set(
      (camera.targetX + 0.5) * this.tileSize,
      (camera.targetY + 0.5) * this.tileSize,
    );
    this.world.position.set(
      Math.round(this.app.renderer.width / 2),
      Math.round(this.app.renderer.height / 2 + camera.trailOffsetY),
    );
    this.world.rotation = camera.headingCurrent;
    this.world.scale.set(camera.zoom);
    // Post-rotation: keep wall stacks pointing screen-up, then rebuild
    // zIndex from rotated tile Y so paint order stays correct.
    this.syncRaisedOffsets();
    this.applyDepthSort();

    // Night vision: on a toggle, force a full re-tint (the light→colour curve
    // changes). The green/blowout colouring is per-tile in `nvTint`.
    this.nightVision = view.nightVision;
    if (this.nightVision !== this.lastNvApplied) {
      this.lastNvApplied = this.nightVision;
      this.lastLightVersion = -1;
    }

    // Lighting: re-tint tiles + fixtures whenever the light field changes
    // (breaker toggle / map load). Cheap no-op on frames where it hasn't.
    this.applyLighting(map);
    // Gunfire light — additive warm glow at each live muzzle flash.
    this.updateMuzzleGlow(map);

    // Sound field: multiply each affected tile glyph's own colour toward the
    // sound hue by the cell intensity (bright at source, faint at edge). This
    // recolours the tile rather than laying a fill quad on top. Reset last
    // frame's tinted tiles to their LIT base first (so sound layers over
    // lighting). Fog reveals these tiles (see fog.update below) so the region
    // is equally bright inside/outside the FOV.
    if (this.mapSprites) {
      const fg = this.mapSprites.fgByIdx;
      const baseArr = this.mapSprites.baseFgByIdx;
      // Reset last frame's dynamically-tinted tiles (sound + flashlight) to lit base.
      for (const idx of this.prevSoundTiles) {
        const s = fg[idx];
        const base = this.litFgByIdx[idx] ?? baseArr[idx];
        if (s && base !== null && base !== undefined) s.tint = base;
      }
      this.prevSoundTiles.length = 0;
      for (const idx of this.prevFlashTiles) {
        const s = fg[idx];
        const base = this.litFgByIdx[idx] ?? baseArr[idx];
        if (s && base !== null && base !== undefined) s.tint = base;
      }
      this.prevFlashTiles.length = 0;
      for (const idx of this.prevVisionTiles) {
        const s = fg[idx];
        const base = this.litFgByIdx[idx] ?? baseArr[idx];
        if (s && base !== null && base !== undefined) s.tint = base;
      }
      this.prevVisionTiles.length = 0;
      // Flashlight: brighten the tiles a beam reaches by their TOTAL light —
      // exactly like room lighting (no separate overlay). Under NV this is a
      // no-op tint (nvTint ignores light); the glare veil handles the white-out.
      const flField = map.flashlightField;
      for (let idx = 0; idx < flField.length; idx++) {
        if ((flField[idx] ?? 0) <= 0.2) continue;
        const s = fg[idx];
        const base = baseArr[idx];
        if (s && base !== null && base !== undefined) {
          s.tint = this.tintByLight(base, lightLevelAt(map, idx % map.width, Math.floor(idx / map.width)));
          this.prevFlashTiles.push(idx);
        }
      }
      // Sound field: multiply each affected tile toward the sound hue.
      for (const [idx, cell] of soundField) {
        const s = fg[idx];
        const base = this.litFgByIdx[idx] ?? baseArr[idx];
        if (s && base !== null && base !== undefined) {
          s.tint = multiplyTint(base, cell.color, cell.intensity);
          this.prevSoundTiles.push(idx);
        }
      }
      // Guard vision cones: BRIGHTEN each watched tile toward a bright blue
      // (screen blend, not multiply) so cone tiles glow brighter than the
      // surrounding floor — a high-contrast highlight rather than a darkening
      // wash. Layered ON TOP of the current tint (`s.tint`, post-lighting/sound);
      // screen keeps the glyph fully readable and each tile's own hue showing.
      for (const [idx, cell] of visionField) {
        const s = fg[idx];
        if (!s) continue;
        s.tint = brightenTint(s.tint as number, cell.color, cell.intensity);
        this.prevVisionTiles.push(idx);
      }
    }

    // Reap sprites whose entities no longer exist.
    for (const id of this.sprites.keys()) {
      if (!world.entities.has(id)) {
        const s = this.sprites.get(id);
        if (s) {
          s.glyph.destroy();
          s.halo.destroy();
          if (s.bg) s.bg.destroy();
          if (s.legsGlyph) s.legsGlyph.destroy();
          if (s.legsHalo) s.legsHalo.destroy();
          if (s.weaponGlyph) s.weaponGlyph.destroy();
        }
        this.sprites.delete(id);
      }
    }

    // Update guard ghost sprites (last-seen desaturated marks). Draw before
    // the live entity pass so a live guard overwrites its own ghost.
    this.updateGuardGhosts(guards, fov, map);

    // Entities.
    for (const e of world.entities.values()) {
      const isPlayer = e.id === player.id;
      // Non-player entities are hidden if their tile isn't currently visible;
      // they'll be surfaced via a ghost sprite instead (drawn above).
      let entry = this.sprites.get(e.id);

      let renderVisible = true;
      let renderTint = e.fg;
      let markedGlow = false;
      const renderX = e.visualX;
      const renderY = e.visualY;

      if (!isPlayer) {
        // For guards (only non-player entity kind in phase 3):
        const g = guards.find((gu) => gu.id === e.id);
        if (g) {
          // Dead player = free-fly spectator: it sees EVERYTHING, so every guard
          // is drawn at its true tile/colour regardless of cover, FOV or darkness.
          const spectating = player.body.dead;
          markedGlow = g.marked && !g.body.dead && g.everSeenByPlayer;
          const idxNow = g.y * map.width + g.x;
          // A guard crouched behind cover (hiddenByCover) reads as not-seen,
          // falling through to the ghost/hidden path.
          const seenNow = spectating || (fov.isCurrentlyVisible(idxNow) && !g.hiddenByCover);
          const audibleUntil = player.hearingUntil.get(g.id);
          const audibleNow = audibleUntil !== undefined && audibleUntil > world.ticks;
          if (g.body.dead) {
            // Dead guards remain visible if seen ever, at their fixed tile — but
            // a spectator sees every corpse regardless.
            renderVisible = g.everSeenByPlayer || spectating;
            if (!seenNow) renderTint = desaturate(e.fg);
          } else if (seenNow) {
            renderVisible = true;
            renderTint = e.fg;
          } else if (audibleNow) {
            // Sound reveal: a guard making noise inside the player's
            // hearing range renders at its true position with an
            // enemy-accent tint even if cover is breaking line of sight.
            renderVisible = true;
            renderTint = 0xff3caa;
          } else if (g.marked && g.everSeenByPlayer) {
            renderVisible = true;
            renderTint = desaturate(e.fg);
          } else {
            // Handled by the ghost pass; hide the live sprite.
            renderVisible = false;
          }
          // In pitch darkness the guard isn't rendered at all (you can't see it)
          // — unless the player's night vision is on, or you're a spectator.
          if (!g.body.dead && !spectating && isUnlit(map, g.x, g.y) && !this.nightVision) renderVisible = false;
        }
      }

      if (!entry) {
        if (!renderVisible) continue;
        entry = this.attach(e);
      }

      entry.glyph.visible = renderVisible;
      entry.halo.visible = renderVisible;
      if (entry.bg) entry.bg.visible = renderVisible && e.bg >= 0;
      if (entry.legsGlyph) entry.legsGlyph.visible = renderVisible;
      if (entry.legsHalo) entry.legsHalo.visible = renderVisible;
      if (!renderVisible) continue;

      if (entry.currentChar !== e.glyph) this.setGlyph(entry, e.glyph);

      const dx = lerp(e.prevVisualX, renderX, alpha);
      const dy = lerp(e.prevVisualY, renderY, alpha);
      const px = dx * this.tileSize;
      const py = dy * this.tileSize;

      // Animations layered on top of the base tint:
      //   facing → rotate the glyph so its TOP points where the entity looks
      //   flash → lerp tint toward white
      //   hit-shake → tiny position jitter
      //   attack skew → sprite.skew.x lean
      //   tilt → sprite.rotation up to 90° when on-side
      let finalTint = renderTint;
      // Damage glow: `hitShake` is only set on taking damage (never by the
      // muzzle flash), so it flags "just hit" — glow RED while it decays. Sustained
      // fire keeps resetting it, so a guard under fire glows red continuously.
      // The plain muzzle/strike flash (flashTimer, no hit) stays white.
      // Red damage glow — but NOT for the janitor (a non-combatant keeps its
      // own colour when hurt; it isn't a hostile).
      if (e.hitShake > 0 && !e.isJanitor) finalTint = mixToward(finalTint, 0xff2323, Math.min(1, e.hitShake));
      else if (e.flashTimer > 0) finalTint = flashMix(finalTint, e.flashTimer);
      // Lighting dims entities standing in shadow so they're harder to pick out
      // (matching the guard-detection falloff). The player keeps a higher floor
      // so you can always make yourself out. Night vision boosts + blows out
      // entities the same way it does tiles.
      const entLight = lightLevelAt(map, e.x, e.y);
      if (this.nightVision) {
        finalTint = nvTint(finalTint); // green; the glare veil above handles blinding
      } else {
        const entBright = lightBrightness(entLight);
        // The player keeps a visibility floor; a dead spectator (seeing the whole
        // floor) gets that same floor on every entity so dark rooms stay legible.
        const floored = isPlayer || player.body.dead;
        finalTint = applyBrightness(finalTint, floored ? Math.max(entBright, 0.5) : entBright);
      }
      // The dead player's roaming spirit reads as a pale, translucent ghost.
      if (isPlayer && player.body.dead) {
        finalTint = GHOST_TINT;
        entry.glyph.alpha = GHOST_ALPHA;
        if (entry.legsGlyph) entry.legsGlyph.alpha = GHOST_ALPHA;
      } else if (isPlayer) {
        entry.glyph.alpha = 1;
        if (entry.legsGlyph) entry.legsGlyph.alpha = 1;
      }
      const shake = this.screenShake && e.hitShake > 0;
      const jitterX = shake ? (Math.random() - 0.5) * 4 * e.hitShake : 0;
      const jitterY = shake ? (Math.random() - 0.5) * 4 * e.hitShake : 0;
      const cx = px + this.tileSize / 2 + jitterX;
      const cy = py + this.tileSize / 2 + jitterY;
      // Walk bob: vertical screen-space oscillation while the entity is
      // visibly moving. Freq scales with distance travelled per second so
      // running steps bob quicker than walking. Frozen (not reset) when
      // idle so the bob doesn't snap-back on stop.
      const speed = Math.hypot(renderX - entry.lastVisualX, renderY - entry.lastVisualY) / Math.max(dt, 1e-6);
      // Dead player = free-fly ghost: it doesn't WALK, it FLOATS — a gentle,
      // constant up/down drift (both directions) with no leg cycle, whether it's
      // moving or hovering.
      const ghost = isPlayer && player.body.dead;
      const moving = speed > 0.01 && !e.tilt; // don't bob a downed entity
      let bobOffsetY = 0;
      if (ghost) {
        entry.bobPhase += dt * GHOST_FLOAT_HZ * 2 * Math.PI;
        bobOffsetY = Math.sin(entry.bobPhase) * GHOST_FLOAT_PX;
      } else if (moving) {
        const freq = BOB_FREQ_BASE_HZ + BOB_FREQ_SPEED_MULT * speed;
        entry.bobPhase += dt * freq * 2 * Math.PI;
        bobOffsetY = -Math.abs(Math.sin(entry.bobPhase)) * BOB_AMPLITUDE_PX;
      }
      entry.lastVisualX = renderX;
      entry.lastVisualY = renderY;

      // Player AND guards are two-tile-tall models: the `glyph` sprite is the
      // TORSO (`@`/`g`), offset one tile screen-up above the LEGS sprite
      // (`ハ`/`メ`) at the tile. The offset is rotation-aware (like raised wall
      // layers) so the torso stays stacked above the legs through world
      // rotation. It collapses to 0 when the entity is CROUCHED (torso tucks
      // within the legs' tile) or DOWNED (`tilt > 0`, so a corpse is one tile).
      const stance = (e as unknown as { stance?: string }).stance;
      // The roaming player-ghost is the BASE two-tile model in default upright
      // orientation — never collapsed/on-its-side and never rotated to the
      // facing you died in (see the rotation overrides below).
      const collapsed = !ghost && (stance === 'crouch' || e.tilt > 0);
      let torsoOffX: number;
      let torsoOffY: number;
      // Crouched side-peek: instead of standing the torso UP, poke it out to the
      // side (world-space lean direction) past the cover edge — legs stay put.
      const leanDx = isPlayer ? (player.coverLeanDx ?? 0) : 0;
      const leanDy = isPlayer ? (player.coverLeanDy ?? 0) : 0;
      if (collapsed && (leanDx !== 0 || leanDy !== 0)) {
        // World-space offset (rotates with the world, since the lean is a
        // world direction around the corner), scaled to ~one tile.
        torsoOffX = leanDx * CONFIG.render.playerTorsoOffsetPx;
        torsoOffY = leanDy * CONFIG.render.playerTorsoOffsetPx;
      } else {
        const off = collapsed ? 0 : CONFIG.render.playerTorsoOffsetPx;
        torsoOffX = -off * Math.sin(this.world.rotation);
        torsoOffY = -off * Math.cos(this.world.rotation);
      }

      // Sprint-vault "jump": while vaulting, lift the whole body in an arc that
      // crests as it clears the cover, and tuck the LEGS up to torso level so it
      // reads as a jump rather than a slide. hop*/legLift are screen-up offsets.
      let hopX = 0;
      let hopY = 0;
      let legLift = 0;
      if (isPlayer && (player.vaultX ?? -1) >= 0) {
        const tx = player.vaultX!;
        const ty = player.vaultY!;
        const dirX = Math.sign(tx - dx);
        const dirY = Math.sign(ty - dy);
        // The cover tile is one tile back from the landing tile along travel.
        const distToWall = Math.abs(dx - (tx - dirX)) + Math.abs(dy - (ty - dirY));
        const arc = Math.max(0, 1 - distToWall); // 0..1, peaks over the cover
        const hop = arc * CONFIG.render.vaultHopPx;
        hopX = -hop * Math.sin(this.world.rotation);
        hopY = -hop * Math.cos(this.world.rotation);
        legLift = CONFIG.render.playerTorsoOffsetPx; // legs come up to the torso
      }

      entry.glyph.tint = finalTint;
      entry.glyph.anchor.set(0.5, 0.5);
      entry.glyph.x = cx + torsoOffX + hopX;
      entry.glyph.y = cy + bobOffsetY + torsoOffY + hopY;
      // Rotate so the glyph's top points along facing, then add the on-side
      // tilt for downed entities. The ghost ignores both (upright default).
      entry.glyph.rotation = ghost ? 0 : facingAngle(e.facingDx, e.facingDy) + e.tilt * (Math.PI / 2);
      entry.glyph.skew.x = e.attackSkew;
      // Tag base for the rotation-aware depth sort. Base is the TILE (cx, cy),
      // not the offset torso position, so the whole model sorts as one tile;
      // the torso's higher __layerIdx keeps it painted over the legs.
      const gtag = entry.glyph as unknown as { __baseX: number; __baseY: number; __layerIdx: number };
      gtag.__baseX = cx;
      gtag.__baseY = cy;
      gtag.__layerIdx = 7;

      entry.halo.tint = finalTint;
      entry.halo.x = cx + torsoOffX + hopX;
      entry.halo.y = cy + bobOffsetY + torsoOffY + hopY;
      entry.halo.rotation = entry.glyph.rotation;
      // Marked guards get a bright amber glow halo so you can always pick them
      // out; reset to the base halo otherwise.
      if (markedGlow) {
        entry.halo.tint = 0xffcf50;
        entry.halo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST * 2.1;
      } else {
        entry.halo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST;
      }
      const htag = entry.halo as unknown as { __baseX: number; __baseY: number; __layerIdx: number };
      htag.__baseX = cx;
      htag.__baseY = cy;
      htag.__layerIdx = 6;

      // Legs (player + guards): animated two-frame walk cycle (ハ↔メ) whose
      // cadence scales with travel speed; frozen on frame 0 (ハ) when idle.
      // Rendered at the tile, rotated to the LEG direction (movement), not the
      // torso look.
      if (entry.legsGlyph && entry.legsHalo) {
        // The ghost keeps its legs but never animates them (it glides, not walks);
        // living entities run the two-frame walk cycle while moving.
        const animateLegs = moving && !ghost;
        if (animateLegs) {
          const legFreq = LEG_ANIM_BASE_HZ + LEG_ANIM_SPEED_MULT * speed;
          entry.legsPhase += dt * legFreq * 2 * Math.PI;
        }
        const frame = !animateLegs
          ? LEG_ANIM_FRAMES[0]
          : (Math.sin(entry.legsPhase) >= 0 ? LEG_ANIM_FRAMES[0] : LEG_ANIM_FRAMES[1]);
        if (entry.legsChar !== frame) this.setLegsGlyph(entry, frame);

        const legRot = ghost ? 0 : facingAngle(e.legDx, e.legDy) + e.tilt * (Math.PI / 2);
        // During a vault the legs tuck up to torso level (legLift) and ride the
        // hop arc, so the model looks airborne rather than sliding through.
        const legOffX = -legLift * Math.sin(this.world.rotation) + hopX;
        const legOffY = -legLift * Math.cos(this.world.rotation) + hopY;
        entry.legsGlyph.tint = finalTint;
        entry.legsGlyph.x = cx + legOffX;
        entry.legsGlyph.y = cy + bobOffsetY + legOffY;
        entry.legsGlyph.rotation = legRot;
        const ltag = entry.legsGlyph as unknown as { __baseX: number; __baseY: number; __layerIdx: number };
        ltag.__baseX = cx;
        ltag.__baseY = cy;
        ltag.__layerIdx = 5;

        entry.legsHalo.tint = finalTint;
        entry.legsHalo.x = cx + legOffX;
        entry.legsHalo.y = cy + bobOffsetY + legOffY;
        entry.legsHalo.rotation = legRot;
        if (markedGlow) {
          entry.legsHalo.tint = 0xffcf50;
          entry.legsHalo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST * 2.1;
        } else {
          entry.legsHalo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST;
        }
        const lhtag = entry.legsHalo as unknown as { __baseX: number; __baseY: number; __layerIdx: number };
        lhtag.__baseX = cx;
        lhtag.__baseY = cy;
        lhtag.__layerIdx = 4;
      }

      // Held-weapon suit (player only, focus mode): the active weapon's card
      // suit rendered ON the character — the same glyph the stance display shows.
      // For weapons it juts out toward the locked target and rotates to aim at
      // it (the torso already faces the target under focus lock). The heal ♥
      // instead appears held straight up and stays screen-upright (no aim).
      if (entry.weaponGlyph) {
        const wg = entry.weaponGlyph;
        if (isPlayer && player.focusActive) {
          const wIdx = player.activeWeaponIdx;
          const wchar = WEAPON_SUIT_GLYPHS[wIdx];
          if (entry.weaponChar !== wchar) this.setWeaponGlyph(entry, wchar);
          wg.visible = true;
          wg.tint = WEAPON_SUIT_TINTS[wIdx];
          const torsoX = cx + torsoOffX;
          const torsoY = cy + bobOffsetY + torsoOffY;
          const reach = this.tileSize * WEAPON_SUIT_REACH;
          if (player.weapons[wIdx].kind === 'heal') {
            // Held up above the head; cancel the world spin so ♥ stays upright.
            wg.x = torsoX - reach * Math.sin(this.world.rotation);
            wg.y = torsoY - reach * Math.cos(this.world.rotation);
            wg.rotation = -this.world.rotation;
          } else {
            // Extended toward the locked target along the torso's facing.
            const fmag = Math.hypot(e.facingDx, e.facingDy) || 1;
            wg.x = torsoX + (e.facingDx / fmag) * reach;
            wg.y = torsoY + (e.facingDy / fmag) * reach;
            wg.rotation = facingAngle(e.facingDx, e.facingDy);
          }
          const wtag = wg as unknown as { __baseX: number; __baseY: number; __layerIdx: number };
          wtag.__baseX = cx;
          wtag.__baseY = cy;
          wtag.__layerIdx = 8;
        } else {
          wg.visible = false;
        }
      }

      if (entry.bg) {
        if (e.bg < 0) {
          entry.bg.visible = false;
        } else {
          entry.bg.visible = true;
          entry.bg.tint = e.bg;
          entry.bg.x = px;
          entry.bg.y = py;
        }
      }
    }

    this.fog.update(fov, map, soundField, visionField);
    this.updateNvGlare(player, map, fov);
    this.updateSightingGhosts(guards);
    this.updatePlayerCorpse(player);
    this.updateRaisedWallFov(fov, map);
    this.updateAlertMarks(guards, fov, map, player, world.ticks);
    this.updateAlertingMarks(guards, fov, map, player, world.ticks);
    // Sound + guard-vision cones are applied inline above (glyph tint), and the
    // fog reveals both tile sets so they read bright regardless of the FOV.
  }

  /**
   * Reposition raised wall/half-wall sprites so their screen offset
   * always points "up" post-rotation. The world container rotates the
   * whole map; a plain (0, -step) offset in local coords rotates with
   * it and ends up pointing sideways/down when the world is tilted. So
   * we compute a pre-rotated offset in local coords whose rotation
   * evaluates to (0, -step) on screen: (-step * sin θ, -step * cos θ).
   */
  /**
   * Re-tint the static map (ground glyphs + raised wall layers + fixture
   * markers) by the per-tile light field. Only runs when `map.lightVersion`
   * changed, so it's a cheap compare on most frames. Ground lit tints are
   * cached in `litFgByIdx` for the per-frame sound-reset to restore to.
   */
  private applyLighting(map: MapData): void {
    if (!this.mapSprites) return;
    if (map.lightVersion === this.lastLightVersion) return;
    this.lastLightVersion = map.lightVersion;

    const fg = this.mapSprites.fgByIdx;
    const baseArr = this.mapSprites.baseFgByIdx;
    const raised = this.mapSprites.raisedByIdx;
    const halos = this.mapSprites.raisedHalosByIdx;
    if (this.litFgByIdx.length !== fg.length) this.litFgByIdx = new Array(fg.length).fill(null);

    for (let idx = 0; idx < fg.length; idx++) {
      const base = baseArr[idx];
      if (base === null || base === undefined) continue;
      const light = map.lightField[idx] ?? CONFIG.light.max;
      const lit = this.tintByLight(base, light);
      this.litFgByIdx[idx] = lit;
      const s = fg[idx];
      if (s) s.tint = lit;
      // Raised wall/half-wall layers share the tile's base fg (darkened per
      // layer at build). Re-apply light on top of that per-layer darken.
      const rlist = raised[idx];
      if (rlist) {
        for (const sprite of rlist) {
          const li = (sprite as unknown as { __layerZ: number }).__layerZ;
          sprite.tint = this.tintByLight(darkenColor(base, li * STACK_DARKEN_PER_LAYER), light);
        }
      }
      const hlist = halos[idx];
      if (hlist) {
        for (const sprite of hlist) {
          const li = (sprite as unknown as { __layerZ: number }).__layerZ;
          sprite.tint = this.tintByLight(darkenColor(base, li * STACK_DARKEN_PER_LAYER), light);
        }
      }
    }

    // Fixtures: warm + bright when their room's breaker is on, dim + cold off.
    const onById = new Map<number, boolean>();
    for (const r of map.rooms) onById.set(r.id, r.lightsOn);
    for (const f of this.lightFixtures) {
      const on = onById.get(f.roomId) !== false;
      f.sprite.texture = this.atlas.get(on ? '☼' : '✷') ?? Texture.EMPTY;
      f.sprite.tint = on ? 0xfff2c0 : 0x4a5568;
      f.sprite.alpha = on ? 1 : 0.7;
    }
  }

  /** Map a tile/entity base colour by its light level — the normal dim ramp, or
   *  the night-vision green (blinding from bright light is a glare veil, not a
   *  tint — see `updateNvGlare`). */
  private tintByLight(base: number, light: number): number {
    return this.nightVision ? nvTint(base) : applyBrightness(base, lightBrightness(light));
  }

  /**
   * Draw the night-vision glare veil: a translucent WHITE quad over every
   * currently-visible tile whose light exceeds the NV ceiling, alpha = the tile's
   * overexposure (light 6 → effective 16 → 0.6). Because it's drawn ON TOP, the
   * light physically occludes whatever's rendered on the tile — the goggles are
   * blinded. Only visible tiles glare (you can't be dazzled by light you can't
   * see). No-op when night vision is off.
   */
  private updateNvGlare(player: Player, map: MapData, fov: FovMemory): void {
    let gi = 0;
    // Flash-blind whiteout: while blinded the player sees its whole cone as
    // overexposed (~20 light → full white), eroding to 0 over the blind. This is
    // a uniform white veil over every FOV tile, MAX-combined with the per-tile
    // night-vision glare so both read at once.
    const blindA = player.body.dead ? 0 : blindIntensity(player);
    if (this.nightVision || blindA > 0) {
      for (const idx of fov.currentlyVisible) {
        const tx = idx % map.width;
        const ty = Math.floor(idx / map.width);
        let alpha = blindA;
        if (this.nightVision) alpha = Math.max(alpha, nvGlareAlpha(lightLevelAt(map, tx, ty)));
        if (alpha <= 0.02) continue;
        const s = this.getGlare(gi++);
        s.x = tx * this.tileSize;
        s.y = ty * this.tileSize;
        s.alpha = Math.min(1, alpha);
        s.visible = true;
      }
    }
    for (let k = gi; k < this.glarePool.length; k++) this.glarePool[k]!.visible = false;
  }

  private getGlare(i: number): Sprite {
    while (i >= this.glarePool.length) {
      const s = new Sprite(Texture.WHITE);
      s.setSize(this.tileSize, this.tileSize);
      s.tint = 0xffffff;
      s.visible = false;
      this.glareLayer.addChild(s);
      this.glarePool.push(s);
    }
    return this.glarePool[i]!;
  }

  /** Player-visible "seen" ghost: a single pale '@' at the FRESHEST sighting on
   *  the floor. Only one ghost ever shows — a newer sighting (full timer) always
   *  outranks older, fading ones, so no two coexist and the latest replaces the
   *  previous. Fades as the ~5s timer runs out. */
  private updateSightingGhosts(guards: readonly Guard[]): void {
    const maxTicks = Math.max(1, Math.round(CONFIG.awareness.sightingGhostSec * CONFIG.tickHz));
    let freshest: Guard | null = null;
    for (const g of guards) {
      if (g.body.dead || g.sightingGhostTicks <= 0) continue;
      if (g.lastKnownPlayerX === null || g.lastKnownPlayerY === null) continue;
      if (!freshest || g.sightingGhostTicks > freshest.sightingGhostTicks) freshest = g;
    }
    if (freshest) {
      const s = this.getSightingGhost(0);
      s.x = (freshest.lastKnownPlayerX! + 0.5) * this.tileSize;
      s.y = (freshest.lastKnownPlayerY! + 0.5) * this.tileSize;
      s.alpha = 0.25 + 0.55 * Math.min(1, freshest.sightingGhostTicks / maxTicks);
      s.visible = true;
    }
    for (let k = freshest ? 1 : 0; k < this.sightingGhostPool.length; k++) this.sightingGhostPool[k]!.visible = false;
  }

  /** The player's corpse stays pinned to `deathTile` (dark red, laid on its side)
   *  while the player roams as a ghost — so a body is left behind where you fell.
   *  Keeps its LEGS like a downed guard (torso `@` + legs `ハ`, both on their
   *  side, collapsed onto the one tile). Hidden once the player respawns. */
  private updatePlayerCorpse(player: Player): void {
    const tile = player.body.dead ? player.deathTile : null;
    if (!tile) {
      if (this.playerCorpseSprite) this.playerCorpseSprite.visible = false;
      if (this.playerCorpseLegsSprite) this.playerCorpseLegsSprite.visible = false;
      return;
    }
    const scale = this.tileSize / this.atlas.cellH;
    if (!this.playerCorpseSprite) {
      const s = new Sprite(this.atlas.get('@') ?? Texture.EMPTY);
      s.anchor.set(0.5, 0.5);
      s.scale.set(scale);
      s.rotation = Math.PI / 2; // laid on its side — downed
      s.tint = 0x8a1f1f; // dried-blood red
      this.entityFgLayer.addChild(s);
      this.playerCorpseSprite = s;
    }
    if (!this.playerCorpseLegsSprite) {
      const s = new Sprite(this.atlas.get(LEG_ANIM_FRAMES[0]) ?? Texture.EMPTY);
      s.anchor.set(0.5, 0.5);
      s.scale.set(scale);
      s.rotation = Math.PI / 2; // on its side, matching the torso
      s.tint = 0x6e1818; // slightly darker than the torso
      this.entityFgLayer.addChild(s);
      this.playerCorpseLegsSprite = s;
    }
    const cx = (tile.x + 0.5) * this.tileSize;
    const cy = (tile.y + 0.5) * this.tileSize;
    const torso = this.playerCorpseSprite;
    torso.x = cx;
    torso.y = cy;
    torso.visible = true;
    const legs = this.playerCorpseLegsSprite;
    legs.x = cx;
    legs.y = cy;
    legs.visible = true;
  }

  private getSightingGhost(i: number): Sprite {
    while (i >= this.sightingGhostPool.length) {
      const s = new Sprite(this.atlas.get('@') ?? Texture.EMPTY);
      s.anchor.set(0.5, 0.5);
      s.scale.set(this.tileSize / this.atlas.cellH);
      s.tint = 0xdfe8ff; // pale "seen here" silhouette
      s.visible = false;
      this.sightingGhostLayer.addChild(s);
      this.sightingGhostPool.push(s);
    }
    return this.sightingGhostPool[i]!;
  }


  /** Pool one additive glow sprite per live dynamic light (muzzle flash). */
  private updateMuzzleGlow(map: MapData): void {
    const lights = map.dynamicLights;
    for (let i = 0; i < lights.length; i++) {
      let s = this.muzzleSprites[i];
      if (!s) {
        s = new Sprite(this.atlas.get('☼') ?? Texture.EMPTY);
        s.anchor.set(0.5, 0.5);
        s.blendMode = 'add';
        this.muzzleLayer.addChild(s);
        this.muzzleSprites[i] = s;
      }
      const d = lights[i]!;
      const frac = Math.max(0, d.life > 0 ? d.ttl / d.life : 0);
      s.visible = frac > 0;
      s.tint = 0xffdf9a;
      s.alpha = 0.8 * frac;
      // Scale to roughly cover the flash radius; the atlas cell is ~1 tile.
      // Kept small (~half the earlier size) so the flash reads as a spark.
      const sc = (this.tileSize / this.atlas.cellH) * d.radius * 0.8;
      s.scale.set(sc);
      s.x = (d.x + 0.5) * this.tileSize;
      s.y = (d.y + 0.5) * this.tileSize;
    }
    for (let i = lights.length; i < this.muzzleSprites.length; i++) this.muzzleSprites[i]!.visible = false;
  }

  private syncRaisedOffsets(): void {
    if (!this.mapSprites) return;
    const s = Math.sin(this.world.rotation);
    const c = Math.cos(this.world.rotation);
    const raised = this.mapSprites.raisedByIdx;
    const halos = this.mapSprites.raisedHalosByIdx;
    const step = LAYER_Z_STEP_PX;
    for (let idx = 0; idx < raised.length; idx++) {
      const list = raised[idx];
      if (!list) continue;
      for (const sprite of list) {
        const tag = sprite as unknown as { __baseX: number; __baseY: number; __layerZ: number };
        const off = tag.__layerZ * step;
        sprite.x = tag.__baseX + -off * s;
        sprite.y = tag.__baseY + -off * c;
      }
    }
    for (let idx = 0; idx < halos.length; idx++) {
      const list = halos[idx];
      if (!list) continue;
      for (const sprite of list) {
        const tag = sprite as unknown as { __baseX: number; __baseY: number; __layerZ: number };
        const off = tag.__layerZ * step;
        sprite.x = tag.__baseX + -off * s;
        sprite.y = tag.__baseY + -off * c;
      }
    }
  }

  /**
   * Rotate-aware depth sort. zIndex derives from the sprite's TILE-base
   * position rotated by the world heading — so raised wall sprites stay
   * grouped with their ground tile in the paint order (via a small
   * per-sprite __layerIdx tiebreaker). Rebuilt each frame in O(sprites).
   */
  private applyDepthSort(): void {
    const s = Math.sin(this.world.rotation);
    const c = Math.cos(this.world.rotation);
    const K = 100;
    const process = (container: Container): void => {
      for (const child of container.children) {
        const tag = child as unknown as { __baseX?: number; __baseY?: number; __layerIdx?: number };
        const bx = tag.__baseX ?? child.x;
        const by = tag.__baseY ?? child.y;
        const ry = bx * s + by * c;
        const li = tag.__layerIdx ?? 0;
        child.zIndex = Math.round(ry * K) + li;
      }
    };
    process(this.mapFgLayer);
    process(this.mapBloomLayer);
    process(this.entityFgLayer);
    process(this.entityBloomLayer);
    process(this.bullets.container);
  }

  /**
   * Alpha-mask every raised wall/half-wall stack sprite (and its halo) by
   * the tile's FOV state. Unseen tiles get 0 alpha on their raised layers
   * so the wall stack disappears entirely. Seen-but-not-currently-visible
   * tiles get a dim residual so the memory is still readable.
   */
  private updateRaisedWallFov(fov: FovMemory, map: MapData): void {
    if (!this.mapSprites) return;
    const raised = this.mapSprites.raisedByIdx;
    const halos = this.mapSprites.raisedHalosByIdx;
    const seenMult = 0.35;
    const litHalo = BLOOM_ALPHA * STACK_HALO_BOOST;
    for (let idx = 0; idx < raised.length; idx++) {
      const list = raised[idx];
      if (!list) continue;
      const visible = fov.isCurrentlyVisible(idx);
      const seen = fov.isSeen(idx);
      const litAlpha = visible ? STACK_ALPHA_RAISED : seen ? STACK_ALPHA_RAISED * seenMult : 0;
      for (const s of list) s.alpha = litAlpha;
      const hlist = halos[idx];
      if (hlist) {
        const ha = visible ? litHalo : seen ? litHalo * seenMult : 0;
        for (const h of hlist) h.alpha = ha;
      }
    }
    // Consume vars so unused-checks don't fire when map is used only for indexing.
    void map;
  }

  /**
   * Whether the player can perceive `guard` RIGHT NOW — its tile is in the
   * live vision cone (and it isn't crouched behind cover), or it's audible
   * within hearing range this tick. This is the gate for surfacing a guard's
   * behavioural state (alert / alerting others): if you can neither see nor
   * hear the guard, you don't get to see what it's doing — even if it's a
   * marked (tagged) guard whose position is tracked through walls.
   */
  private playerPerceivesGuard(
    g: Guard,
    fov: FovMemory,
    map: MapData,
    player: Player,
    ticks: number,
  ): boolean {
    if (g.body.dead) return false;
    const seenNow = fov.isCurrentlyVisible(g.y * map.width + g.x) && !g.hiddenByCover;
    const audibleUntil = player.hearingUntil.get(g.id);
    const audibleNow = audibleUntil !== undefined && audibleUntil > ticks;
    return seenNow || audibleNow;
  }

  private updateAlertMarks(
    guards: readonly Guard[],
    fov: FovMemory,
    map: MapData,
    player: Player,
    ticks: number,
  ): void {
    for (const s of this.alertMarks.values()) s.visible = false;
    for (const g of guards) {
      if (g.body.dead || g.state !== 'alert') continue;
      // Only surface the alert '!' when the player can actually see or hear the
      // guard — never merely because it's tagged/marked.
      if (!this.playerPerceivesGuard(g, fov, map, player, ticks)) continue;
      let s = this.alertMarks.get(g.id);
      if (!s) {
        const tex = this.atlas.get('!') ?? Texture.EMPTY;
        s = new Sprite(tex);
        s.anchor.set(0.5, 0.5);
        const scale = (this.tileSize / this.atlas.cellH) * 0.7;
        s.scale.set(scale);
        s.tint = 0xff3030;
        this.entityFgLayer.addChild(s);
        this.alertMarks.set(g.id, s);
      }
      // Exponent position: top-right of the guard glyph, centered anchor.
      s.x = g.visualX * this.tileSize + this.tileSize * 0.85;
      s.y = g.visualY * this.tileSize + this.tileSize * 0.15;
      s.visible = true;
    }
  }

  /** '#' icon above a guard that is currently alerting other guards (broadcast
   *  or power-restore duty). Amber, hovering over the head. */
  private updateAlertingMarks(
    guards: readonly Guard[],
    fov: FovMemory,
    map: MapData,
    player: Player,
    ticks: number,
  ): void {
    for (const s of this.alertingMarks.values()) s.visible = false;
    for (const g of guards) {
      if (g.body.dead || g.alertingTimer <= 0) continue;
      // "Alerting others" is a behaviour you only learn by seeing or hearing the
      // guard do it — gate on live perception, not on the marked/tag flag.
      if (!this.playerPerceivesGuard(g, fov, map, player, ticks)) continue;
      let s = this.alertingMarks.get(g.id);
      if (!s) {
        const tex = this.atlas.get('#') ?? Texture.EMPTY;
        s = new Sprite(tex);
        s.anchor.set(0.5, 0.5);
        s.scale.set((this.tileSize / this.atlas.cellH) * 0.8);
        s.tint = 0xffcf50;
        this.entityFgLayer.addChild(s);
        this.alertingMarks.set(g.id, s);
      }
      // Above the guard's head (up-left of the alert '!' so they don't overlap).
      s.x = g.visualX * this.tileSize + this.tileSize * 0.15;
      s.y = g.visualY * this.tileSize - this.tileSize * 0.55;
      s.visible = true;
    }
  }

  private updateGuardGhosts(
    guards: readonly Guard[],
    fov: FovMemory,
    map: MapData,
  ): void {
    // Hide all first, then re-show for eligible guards.
    for (const s of this.ghostSprites.values()) s.visible = false;
    for (const g of guards) {
      if (!g.everSeenByPlayer) continue;
      if (g.body.dead) continue;
      if (g.marked) continue; // marked guards render at current pos via main pass
      const seenNow = fov.isCurrentlyVisible(g.y * map.width + g.x);
      if (seenNow) continue;
      // Draw a desaturated ghost at last-seen tile.
      let s = this.ghostSprites.get(g.id);
      const scale = this.tileSize / this.atlas.cellH;
      if (!s) {
        const tex = this.atlas.get(g.lastSeenGlyph) ?? Texture.EMPTY;
        s = new Sprite(tex);
        s.anchor.set(0.5, 0.5);
        s.scale.set(scale);
        this.entityFgLayer.addChild(s);
        this.ghostSprites.set(g.id, s);
      } else {
        s.texture = this.atlas.get(g.lastSeenGlyph) ?? Texture.EMPTY;
        s.scale.set(scale);
      }
      s.tint = desaturate(g.lastSeenFg);
      s.alpha = 0.65;
      s.x = g.lastSeenX * this.tileSize + this.tileSize / 2;
      s.y = g.lastSeenY * this.tileSize + this.tileSize / 2;
      s.visible = true;
    }
  }

  private attach(e: Entity): SpriteEntry {
    const tex = this.atlas.get(e.glyph) ?? Texture.EMPTY;
    const glyph = new Sprite(tex);
    // Uniform scale — preserves the font's aspect ratio (readability wins
    // over pixel-perfect square tiles). Anchor centered so rotation +
    // skew from animations rotate around the glyph center.
    const scale = this.tileSize / this.atlas.cellH;
    glyph.anchor.set(0.5, 0.5);
    glyph.scale.set(scale);
    glyph.tint = e.fg;
    // Two-tile model: the torso stacks ABOVE its legs (idx 7 vs legs 5).
    (glyph as unknown as { __layerIdx: number }).__layerIdx = 7;
    this.entityFgLayer.addChild(glyph);

    // Halo — additive-blended larger copy for neon bloom. Hero glyphs
    // (player, enemies) get a boosted halo.
    const halo = new Sprite(tex);
    halo.anchor.set(0.5, 0.5);
    halo.scale.set(scale * BLOOM_SCALE);
    halo.tint = e.fg;
    halo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST;
    halo.blendMode = 'add';
    (halo as unknown as { __layerIdx: number }).__layerIdx = 6;
    this.entityBloomLayer.addChild(halo);

    let bg: Sprite | null = null;
    if (e.bg >= 0) {
      bg = new Sprite(Texture.WHITE);
      bg.setSize(this.tileSize, this.tileSize);
      bg.tint = e.bg;
      this.entityBgLayer.addChild(bg);
    }

    // Every entity (player + guards) is two-tile: a second sprite for the LEGS
    // (ハ/メ), drawn at the tile (offset 0) beneath the torso. Sits at the
    // ground level (idx 5 / halo 4) so the torso (idx 7) paints on top.
    const legTex = this.atlas.get(LEG_ANIM_FRAMES[0]) ?? Texture.EMPTY;
    const legsGlyph = new Sprite(legTex);
    legsGlyph.anchor.set(0.5, 0.5);
    legsGlyph.scale.set(scale);
    legsGlyph.tint = e.fg;
    (legsGlyph as unknown as { __layerIdx: number }).__layerIdx = 5;
    this.entityFgLayer.addChild(legsGlyph);

    const legsHalo = new Sprite(legTex);
    legsHalo.anchor.set(0.5, 0.5);
    legsHalo.scale.set(scale * BLOOM_SCALE);
    legsHalo.tint = e.fg;
    legsHalo.alpha = BLOOM_ALPHA * BLOOM_HERO_BOOST;
    legsHalo.blendMode = 'add';
    (legsHalo as unknown as { __layerIdx: number }).__layerIdx = 4;
    this.entityBloomLayer.addChild(legsHalo);

    // Held-weapon suit glyph — drawn ABOVE the torso (idx 8), hidden until the
    // player is in focus mode.
    const weaponGlyph = new Sprite(Texture.EMPTY);
    weaponGlyph.anchor.set(0.5, 0.5);
    weaponGlyph.scale.set(scale);
    weaponGlyph.visible = false;
    (weaponGlyph as unknown as { __layerIdx: number }).__layerIdx = 8;
    this.entityFgLayer.addChild(weaponGlyph);

    const entry: SpriteEntry = {
      glyph,
      halo,
      bg,
      currentChar: e.glyph,
      glyphOffsetX: 0, // Full-tile stretch — no horizontal centering offset needed.
      bobPhase: 0,
      lastVisualX: e.visualX,
      lastVisualY: e.visualY,
      legsGlyph,
      legsHalo,
      legsChar: LEG_ANIM_FRAMES[0],
      legsPhase: 0,
      weaponGlyph,
      weaponChar: '',
    };
    this.sprites.set(e.id, entry);
    return entry;
  }

  private setGlyph(entry: SpriteEntry, ch: string): void {
    const tex = this.atlas.get(ch) ?? Texture.EMPTY;
    entry.glyph.texture = tex;
    entry.halo.texture = tex;
    entry.currentChar = ch;
  }

  private setLegsGlyph(entry: SpriteEntry, ch: string): void {
    const tex = this.atlas.get(ch) ?? Texture.EMPTY;
    if (entry.legsGlyph) entry.legsGlyph.texture = tex;
    if (entry.legsHalo) entry.legsHalo.texture = tex;
    entry.legsChar = ch;
  }

  private setWeaponGlyph(entry: SpriteEntry, ch: string): void {
    if (!entry.weaponGlyph) return;
    entry.weaponGlyph.texture = this.atlas.get(ch) ?? Texture.EMPTY;
    entry.weaponChar = ch;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Pixi rotation (CW-positive, screen y-down) that turns a glyph — whose top
 * points screen-up (north) at rotation 0 — so its top points along the facing
 * vector `(fx, fy)`. north(0,-1)→0, east(1,0)→+90°, south(0,1)→180°,
 * west(-1,0)→−90°. Returns 0 for a zero vector.
 */
function facingAngle(fx: number, fy: number): number {
  if (fx === 0 && fy === 0) return 0;
  return Math.atan2(fx, -fy);
}

/** Blend `color` toward `target` by amount `t` in [0, 1] (per-channel lerp). */
function mixToward(color: number, target: number, t: number): number {
  const clamped = Math.max(0, Math.min(1, t));
  const r = (color >> 16) & 0xff, g = (color >> 8) & 0xff, b = color & 0xff;
  const tr = (target >> 16) & 0xff, tg = (target >> 8) & 0xff, tb = target & 0xff;
  const nr = Math.round(r + (tr - r) * clamped);
  const ng = Math.round(g + (tg - g) * clamped);
  const nb = Math.round(b + (tb - b) * clamped);
  return (nr << 16) | (ng << 8) | nb;
}

/** Blend a tinted color toward white by amount `t` in [0, 1]. */
function flashMix(color: number, t: number): number {
  return mixToward(color, 0xffffff, t);
}

/**
 * Multiply-blend a tile's base colour toward a sound hue, modulated by
 * `amount` in [0, 1]. `amount` 0 → base unchanged; 1 → full per-channel
 * multiply (base ⊗ hue). This recolours the tile's own glyph by the sound
 * hue instead of adding a fill on top.
 */
function multiplyTint(base: number, hue: number, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  const br = (base >> 16) & 0xff, bg = (base >> 8) & 0xff, bb = base & 0xff;
  const hr = (hue >> 16) & 0xff, hg = (hue >> 8) & 0xff, hb = hue & 0xff;
  // Per-channel multiply, then lerp base → multiplied by the amount.
  const mr = (br * hr) / 255, mg = (bg * hg) / 255, mb = (bb * hb) / 255;
  const nr = Math.round(br + (mr - br) * t);
  const ng = Math.round(bg + (mg - bg) * t);
  const nb = Math.round(bb + (mb - bb) * t);
  return (nr << 16) | (ng << 8) | nb;
}

/**
 * BRIGHTEN a base colour toward `color` via a SCREEN blend, lerped by `amount`.
 * Unlike `multiplyTint` (which only darkens), screen can only lighten — so a
 * cone tile ends up brighter than the surrounding untinted floor, creating
 * contrast rather than a dark wash. The glyph stays readable (its shape is
 * untouched) and the base tile's own hue still modulates the result, so
 * differently-coloured props under a cone stay distinguishable.
 */
function brightenTint(base: number, color: number, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  const br = (base >> 16) & 0xff, bg = (base >> 8) & 0xff, bb = base & 0xff;
  const cr = (color >> 16) & 0xff, cg = (color >> 8) & 0xff, cb = color & 0xff;
  // Screen blend: 255 - (255-a)(255-b)/255, then lerp base → screened by amount.
  const sr = 255 - ((255 - br) * (255 - cr)) / 255;
  const sg = 255 - ((255 - bg) * (255 - cg)) / 255;
  const sb = 255 - ((255 - bb) * (255 - cb)) / 255;
  const nr = Math.round(br + (sr - br) * t);
  const ng = Math.round(bg + (sg - bg) * t);
  const nb = Math.round(bb + (sb - bb) * t);
  return (nr << 16) | (ng << 8) | nb;
}

/**
 * Night-vision colour for a base tint. NV is MONOCHROME: the base hue is
 * discarded for a phosphor green whose brightness tracks the base glyph's
 * luminance (so walls vs. floor still read by relative brightness), amplified so
 * even a pitch-black tile glows green. The OVEREXPOSURE from bright light is NOT
 * baked in here — it's a separate white glare veil drawn over the tile (see
 * `updateNvGlare`), so intense light actually occludes what's rendered rather
 * than just recolouring it.
 */
function nvTint(base: number): number {
  const gi = 0.4 + 0.6 * luminance01(base); // green intensity from base brightness
  return packRgb01(0.05 * gi, 0.9 * gi, 0.18 * gi);
}

/** White glare-veil opacity for a tile at `light` under night vision: 0 at
 *  effective `max` (=10), ramping to 1 (fully blinding) at `max + nvBoost`
 *  (=20). So light 6 → effective 16 → 0.6 veil. */
function nvGlareAlpha(light: number): number {
  const over = (light + CONFIG.light.nvBoost - CONFIG.light.max) / CONFIG.light.nvBoost;
  return over < 0 ? 0 : over > 1 ? 1 : over;
}

/** Relative luminance of a 0xRRGGBB colour, 0..1. */
function luminance01(color: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return (0.3 * r + 0.59 * g + 0.11 * b) / 255;
}

/** Pack three 0..1 channels into 0xRRGGBB (clamped). */
function packRgb01(r: number, g: number, b: number): number {
  const c = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

/** Map a light level (0..CONFIG.light.max) to a render brightness 0..1, with a
 *  floor so a fully-dark tile is still faintly legible. */
function lightBrightness(light: number): number {
  const t = Math.max(0, Math.min(1, light / CONFIG.light.max));
  return CONFIG.light.renderFloor + (1 - CONFIG.light.renderFloor) * t;
}

/** Scale every channel of a 0xRRGGBB color by `b` (0..1). */
function applyBrightness(color: number, b: number): number {
  const k = Math.max(0, Math.min(1, b));
  const r = (((color >> 16) & 0xff) * k) & 0xff;
  const g = (((color >> 8) & 0xff) * k) & 0xff;
  const bl = ((color & 0xff) * k) & 0xff;
  return (r << 16) | (g << 8) | bl;
}

/** Darken a 0xRRGGBB color by fraction f (0 = no change, 1 = black). */
function darkenColor(color: number, f: number): number {
  return applyBrightness(color, 1 - f);
}

/** Blend a color toward neutral gray so out-of-FOV entities read as memory. */
function desaturate(color: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const gray = (r * 0.3 + g * 0.59 + b * 0.11) | 0;
  const mix = 0.7;
  const nr = Math.round(r * (1 - mix) + gray * mix);
  const ng = Math.round(g * (1 - mix) + gray * mix);
  const nb = Math.round(b * (1 - mix) + gray * mix);
  return (nr << 16) | (ng << 8) | nb;
}
